import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, CircleCheck, LoaderCircle, Plug } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Button } from '../../components/Button';
import { StatusBadge } from '../../components/StatusBadge';
import { useTimeFormat } from '../../lib/useTimeFormat';
import { useAuth } from '../auth/AuthProvider';
import {
  connectionInfo,
  devicesKey,
  identifying,
  identitySourceLabels,
  listStreams,
  probeDevice,
  probeErrorLabels,
  statusView,
  streamsKey,
  type Device,
  type DeviceStream,
  type ProbeReport,
} from './api';

interface Props {
  device: Device;
  canManage: boolean;
}

/** Connection status, connection test (ONVIF or RTSP) and media profiles. */
export function DeviceConnection({ device, canManage }: Props) {
  const { request } = useAuth();
  const fmt = useTimeFormat();
  const qc = useQueryClient();
  const [report, setReport] = useState<ProbeReport | null>(null);
  const current = report?.device ?? device;
  const st = statusView[current.status];

  const streams = useQuery({
    queryKey: streamsKey(device.id),
    queryFn: () => listStreams(request, device.id),
  });

  const probe = useMutation({
    mutationFn: () => probeDevice(request, device.id),
    onSuccess: async (r) => {
      setReport(r);
      qc.setQueryData(streamsKey(device.id), r.ok ? r.streams : streams.data);
      await qc.invalidateQueries({ queryKey: devicesKey, exact: false });
    },
  });

  const skewMin = report ? Math.round(report.clock_skew_seconds / 60) : 0;
  const shown = streams.data ?? [];

  return (
    <section aria-labelledby="conn-heading" className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3
            id="conn-heading"
            className="text-2xs font-medium tracking-wide text-fg-subtle uppercase"
          >
            Conexão
          </h3>
          <p className="mt-1 text-[13px] text-fg">{connectionInfo(current).sentence}</p>
          {(current.camera_number ?? null) !== null && (
            <p className="mt-1 text-xs text-fg-muted">
              Número da câmera para outros sistemas:{' '}
              <span className="font-mono text-fg">{current.camera_number}</span> (canal no padrão
              Hikvision,{' '}
              <span className="font-mono">/Streaming/Channels/{current.camera_number}01</span>
              ).
            </p>
          )}
          <p className="mt-1 text-xs text-fg-muted">
            {current.last_probe_at
              ? `Último teste ${fmt.relative(current.last_probe_at)}`
              : 'Conexão ainda não testada.'}
            {current.last_seen_at && ` · visto online ${fmt.relative(current.last_seen_at)}`}
          </p>
        </div>
        <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
      </div>

      {canManage && (
        <Button
          onClick={() => {
            probe.mutate();
          }}
          disabled={probe.isPending}
        >
          {probe.isPending ? (
            <LoaderCircle aria-hidden="true" className="size-4 animate-spin" strokeWidth={2} />
          ) : (
            <Plug aria-hidden="true" className="size-4" strokeWidth={1.75} />
          )}
          {probe.isPending ? 'Testando…' : 'Testar conexão'}
        </Button>
      )}

      <div aria-live="polite">
        {probe.isError && (
          <Result tone="crit">Não foi possível executar o teste. Tente novamente.</Result>
        )}
        {report && !report.ok && report.error_code && (
          <Result tone="crit">{probeErrorLabels[report.error_code]}</Result>
        )}
        {report?.ok && (
          <Result tone="ok">
            Conectado em {report.latency_ms} ms
            {(report.manufacturer || report.model) &&
              ` · ${[report.manufacturer, report.model].filter(Boolean).join(' ')}`}
            {report.firmware && ` · firmware ${report.firmware}`}
            {Math.abs(skewMin) >= 1 && (
              <span className="mt-1 block text-warn">
                O relógio do dispositivo está {Math.abs(skewMin)} min{' '}
                {skewMin > 0 ? 'adiantado' : 'atrasado'}; a diferença foi compensada, mas configure
                NTP no dispositivo para gravações com horário correto.
              </span>
            )}
          </Result>
        )}
        {!report && current.last_error && (
          <Result tone="crit">Último teste falhou: {probeErrorLabels[current.last_error]}</Result>
        )}
      </div>

      {identifying(current) ? (
        <p role="status" className="flex items-center gap-2 text-xs text-fg-muted">
          <LoaderCircle aria-hidden="true" className="size-3.5 animate-spin" strokeWidth={2} />
          Identificando o aparelho (marca, modelo, firmware, série)…
        </p>
      ) : (
        (current.firmware || current.serial_number || current.detected_model) && (
          <dl
            aria-label="Identificação lida do aparelho"
            className="grid grid-cols-2 gap-3 text-[13px]"
          >
            <Fact label="Fabricante e modelo">
              {[current.detected_vendor, current.detected_model].filter(Boolean).join(' ') || '—'}
            </Fact>
            <Fact label="Firmware">{current.firmware || '—'}</Fact>
            <Fact label="Número de série">{current.serial_number || '—'}</Fact>
            <Fact label="MAC">{current.mac_address || '—'}</Fact>
            {current.identity_source && (
              <p className="col-span-2 text-2xs text-fg-subtle">
                Lido do aparelho por {identitySourceLabels[current.identity_source]}
                {current.identity_at && ` ${fmt.relative(current.identity_at)}`}.
              </p>
            )}
          </dl>
        )
      )}

      {shown.length > 0 && <StreamTable streams={shown} />}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-fg-subtle">{label}</dt>
      <dd className="font-mono text-xs break-words text-fg">{children}</dd>
    </div>
  );
}

function Result({ tone, children }: { tone: 'ok' | 'crit'; children: ReactNode }) {
  const Icon = tone === 'ok' ? CircleCheck : CircleAlert;
  return (
    <div
      role={tone === 'crit' ? 'alert' : 'status'}
      className={`flex items-start gap-2 rounded-md border px-3 py-2 text-[13px] ${
        tone === 'ok' ? 'border-ok/30 bg-ok-soft text-fg' : 'border-crit/30 bg-crit-soft text-crit'
      }`}
    >
      <Icon
        aria-hidden="true"
        className={`mt-0.5 size-4 shrink-0 ${tone === 'ok' ? 'text-ok' : ''}`}
        strokeWidth={2}
      />
      <div>{children}</div>
    </div>
  );
}

function StreamTable({ streams }: { streams: DeviceStream[] }) {
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full text-left text-xs">
        <caption className="sr-only">Perfis de vídeo do dispositivo</caption>
        <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
          <tr className="border-b border-line">
            <th scope="col" className="px-3 py-1.5 font-medium">
              Perfil
            </th>
            <th scope="col" className="px-3 py-1.5 font-medium">
              Vídeo
            </th>
            <th scope="col" className="px-3 py-1.5 font-medium">
              RTSP
            </th>
          </tr>
        </thead>
        <tbody>
          {streams.map((s) => (
            <tr key={s.profile_token} className="border-b border-line last:border-0 align-top">
              <td className="px-3 py-2 text-fg">{s.name || s.profile_token}</td>
              <td className="px-3 py-2 whitespace-nowrap text-fg-muted">
                {[
                  s.encoding,
                  s.width && s.height ? `${s.width}×${s.height}` : '',
                  s.frame_rate ? `${s.frame_rate} fps` : '',
                ]
                  .filter(Boolean)
                  .join(' · ') || '—'}
              </td>
              <td className="px-3 py-2 font-mono break-all text-fg-subtle">{s.uri || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
